import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Sparkles, Check, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { marketplaceAPI } from '../services/api';
import { useVertical } from '../contexts/VerticalContext';
import { getErrorMessage } from '../lib/utils';

/**
 * "Recommended add-ons for your industry" — driven by the Vertical Engine.
 *
 * Shows the marketplace apps the current tenant's vertical registry recommends
 * (bundle.marketplace.recommended), annotated with install eligibility from the backend.
 * Renders nothing when there are no recommendations, so it's safe to drop anywhere.
 */
const IndustryRecommendedAddons = () => {
  const { verticalLabel, recommendedApps } = useVertical();
  const [apps, setApps] = useState([]);
  const [installedIds, setInstalledIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [appsRes, instRes] = await Promise.all([
        marketplaceAPI.listApps(),
        marketplaceAPI.installed(),
      ]);
      setApps(appsRes?.data?.apps || []);
      setInstalledIds(new Set((instRes?.data?.installed || []).map((i) => i.app_id)));
    } catch {
      setApps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const recommended = useMemo(() => {
    const recSet = new Set(recommendedApps || []);
    const preferred = apps.filter((row) => recSet.has(row.app?.id));
    // Fall back to a few eligible apps if the registry lists none.
    return preferred.length ? preferred : apps.filter((row) => row.eligible).slice(0, 3);
  }, [apps, recommendedApps]);

  const install = async (appId) => {
    try {
      setBusyId(appId);
      await marketplaceAPI.install(appId);
      toast.success('Add-on installed');
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not install add-on'));
    } finally {
      setBusyId(null);
    }
  };

  if (!loading && recommended.length === 0) return null;

  return (
    <Card className="bg-card border-border">
      <CardHeader className="border-b border-border/50 bg-muted/20">
        <CardTitle className="text-foreground flex items-center gap-2">
          <div className="p-2 bg-primary/10 rounded-lg"><Sparkles className="h-5 w-5 text-primary" /></div>
          Recommended add-ons for {verticalLabel}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading recommendations…
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {recommended.map(({ app, eligible, reasons }) => {
              const isInstalled = installedIds.has(app.id);
              return (
                <div key={app.id} className="rounded-lg border border-border p-4 flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-foreground text-sm">{app.name}</p>
                    <Badge variant="outline" className="shrink-0 text-[10px]">{app.category}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground flex-1">{app.description}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-muted-foreground">
                      {app.pricing_model === 'free' ? 'Free' : (app.price != null ? `$${app.price}` : '—')}
                    </span>
                    {isInstalled ? (
                      <Badge className="gap-1"><Check className="h-3 w-3" /> Installed</Badge>
                    ) : (
                      <Button
                        size="sm"
                        disabled={!eligible || busyId === app.id}
                        title={!eligible && reasons?.length ? reasons.join('; ') : undefined}
                        onClick={() => install(app.id)}
                      >
                        {busyId === app.id ? <Loader2 className="h-4 w-4 animate-spin" /> : (eligible ? 'Install' : 'Upgrade to unlock')}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default IndustryRecommendedAddons;
